import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, BookOpen, Globe, ArrowUpDown, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * AdminPublisherManager — Gestion des éditeurs (publishers) liés à un produit
 * UNIFIED EDITORIAL MODEL: utilise product_publishers (partner_id + role)
 * 
 * Auto-détecte le mode :
 * - productId exists → LIVE mode (API calls directes)
 * - productId null → LOCAL mode (stockage local, parent gère la création post-save)
 */
const AdminOriginalPublisherManager = ({ productId, pendingPublishers = [], onPendingChange }) => {
  const [links, setLinks] = useState([]);
  const [allPublishers, setAllPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedPub, setSelectedPub] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [creating, setCreating] = useState(false);

  const isLiveMode = !!productId;

  const loadData = useCallback(async () => {
    try {
      const pubsRes = await api.get('/publishers');
      setAllPublishers(pubsRes.data || []);
      
      if (isLiveMode) {
        const linksRes = await api.get(`/products/${productId}/publishers`);
        setLinks(linksRes.data || []);
      }
    } catch {
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [productId, isLiveMode]);

  useEffect(() => { loadData(); }, [loadData]);

  // In local mode, derive links from pendingPublishers
  const effectiveLinks = isLiveMode ? links : pendingPublishers.map((p, i) => ({
    id: `pending-${i}`,
    partner_id: p.partner_id,
    name: allPublishers.find(pub => pub.id === p.partner_id)?.name || p.name || 'Éditeur',
    role: 'publisher',
  }));

  const linkedIds = new Set(effectiveLinks.map(l => l.partner_id));
  const availablePublishers = allPublishers.filter(p => !linkedIds.has(p.id));

  const handleAdd = async () => {
    if (!selectedPub) return;
    
    if (isLiveMode) {
      setAdding(true);
      try {
        await api.post(`/products/${productId}/publishers`, {
          product_id: productId,
          partner_id: selectedPub,
          role: 'publisher',
        });
        toast.success('Éditeur ajouté');
        setSelectedPub('');
        await loadData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erreur');
      } finally {
        setAdding(false);
      }
    } else {
      const pub = allPublishers.find(p => p.id === selectedPub);
      const newPending = [...pendingPublishers, { partner_id: selectedPub, name: pub?.name || '' }];
      onPendingChange?.(newPending);
      setSelectedPub('');
      toast.success('Éditeur ajouté (en attente)');
    }
  };

  const handleRemove = async (linkId, idx) => {
    if (isLiveMode) {
      try {
        await api.delete(`/products/${productId}/publishers/${linkId}`);
        toast.success('Éditeur retiré');
        await loadData();
      } catch {
        toast.error('Erreur lors de la suppression');
      }
    } else {
      const newPending = pendingPublishers.filter((_, i) => i !== idx);
      onPendingChange?.(newPending);
      toast.success('Éditeur retiré');
    }
  };

  const handleCreatePublisher = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post('/publishers', {
        name: newName.trim(),
        country: newCountry.trim() || null,
      });
      toast.success(`Éditeur "${res.data.name}" créé`);
      setNewName('');
      setNewCountry('');
      setShowCreate(false);
      
      if (isLiveMode) {
        await api.post(`/products/${productId}/publishers`, {
          product_id: productId,
          partner_id: res.data.id,
          role: 'publisher',
        });
        await loadData();
      } else {
        const newPending = [...pendingPublishers, { partner_id: res.data.id, name: res.data.name }];
        onPendingChange?.(newPending);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de création');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="py-3 text-sm text-slate-400 flex items-center gap-2">
        <div className="animate-spin h-4 w-4 border-2 border-slate-300 border-t-transparent rounded-full" />
        Chargement éditeurs...
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="publisher-manager">
      <Label className="text-sm font-semibold flex items-center gap-2">
        <BookOpen className="h-4 w-4 text-amber-600" />
        Éditeurs
        {effectiveLinks.length > 1 && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700">Coédition</Badge>}
        {!isLiveMode && effectiveLinks.length > 0 && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">En attente</Badge>}
      </Label>

      {/* Publisher links */}
      {effectiveLinks.length > 0 && (
        <div className="space-y-1.5">
          {effectiveLinks.map((link, idx) => (
            <div key={link.id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-100 group" data-testid={`pub-link-${idx}`}>
              <span className="text-xs text-slate-400 w-5 text-center font-mono">{idx + 1}</span>
              <span className="flex-1 text-sm font-medium text-slate-800">{link.name || 'Inconnu'}</span>
              {link.country && (
                <Badge variant="outline" className="text-[10px]">
                  <Globe className="h-3 w-3 mr-0.5" />{link.country}
                </Badge>
              )}
              <Button
                variant="ghost" size="sm"
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600"
                onClick={() => handleRemove(link.id, idx)}
                data-testid={`remove-pub-${idx}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add existing publisher */}
      <div className="flex gap-2">
        <select
          value={selectedPub}
          onChange={e => setSelectedPub(e.target.value)}
          className="flex-1 h-8 px-2 text-sm border border-slate-200 rounded-md bg-white"
          data-testid="op-select"
        >
          <option value="">Sélectionner un éditeur existant...</option>
          {availablePublishers.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}{p.country ? ` (${p.country})` : ''}
            </option>
          ))}
        </select>
        <Button size="sm" className="h-8 gap-1" onClick={handleAdd} disabled={!selectedPub || adding} data-testid="op-add-btn">
          <Plus className="h-3.5 w-3.5" /> Ajouter
        </Button>
      </div>

      {/* Create new publisher inline */}
      {!showCreate ? (
        <button
          onClick={() => setShowCreate(true)}
          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
          data-testid="op-create-toggle"
        >
          <Plus className="h-3 w-3" /> Créer un nouvel éditeur original
        </button>
      ) : (
        <div className="p-3 border border-indigo-200 rounded-lg bg-indigo-50/50 space-y-2">
          <p className="text-xs font-medium text-indigo-700">Nouvel éditeur original</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Nom (ex: Stonemaier Games)"
              className="flex-1 h-8 px-2 text-sm border rounded-md"
              data-testid="op-new-name"
            />
            <input
              type="text"
              value={newCountry}
              onChange={e => setNewCountry(e.target.value)}
              placeholder="Pays (US, FR...)"
              className="w-20 h-8 px-2 text-sm border rounded-md"
              data-testid="op-new-country"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs gap-1" onClick={handleCreatePublisher} disabled={creating || !newName.trim()} data-testid="op-create-btn">
              <Plus className="h-3 w-3" /> Créer et lier
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setShowCreate(false); setNewName(''); setNewCountry(''); }}>
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOriginalPublisherManager;
